import { useMemo, useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { Link } from 'react-router';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useIntegrations, useUrlSearch } from '@/hooks';
import { readMetaChannels } from '@/modules/integrations/meta-contract';
import { ActionLogFilterBar } from '@/modules/social-agent/components/action-log-filter-bar';
import { ActionLogExportDialog } from '@/modules/social-agent/components/action-log-export-dialog';
import { ActionLogDetailPanel } from '@/modules/social-agent/components/action-log-detail-panel';
import { MAX_ACTION_LOG_SEARCH_LENGTH } from '@/modules/social-agent/action-log-constants';
import type { SocialActionLogRow } from '@/modules/social-agent/types';
import { SearchInput } from '@/components/ui/input';
import { ActionLogTable } from '@/modules/social-agent/components/action-log-table';
import { useActionLogs } from '@/modules/social-agent/hooks/use-action-logs';
import {
	EMPTY_ACTION_LOG_FILTERS,
	hasNarrowedActionLog,
	type ActionLogDateRange,
	type ActionLogFilters
} from '@/modules/social-agent/action-log-constants';
import type { FilterableChannel } from '@/modules/social-agent/constants';
import type { SocialActionDecision, SocialActionType } from '@/modules/social-agent/types';
import { PILL_BUTTON } from '@/modules/social-agent/ui-chrome';

/**
 * Action log — what the social media agent actually did.
 *
 * The sibling of the rules page: rules say what SHOULD happen, this says what DID. It is
 * append-only and read-only end to end. There is no delete and no edit here, and there must not
 * be: these rows are the record of what the agent did to customers in public, and a record that
 * can be tidied is not a record.
 */

const ActionLogSkeleton = () => (
	<div className='flex flex-col'>
		<div className='flex h-10 items-center border-y border-gray-300 bg-gray-100 pl-4'>
			<Skeleton className='h-3 w-32' />
		</div>
		{[1, 2, 3, 4, 5, 6].map((row) => (
			<div key={row} className='flex h-14 items-center justify-between border-b border-muted px-4'>
				<div className='flex flex-col gap-1.5'>
					<Skeleton className='h-3 w-24' />
					<Skeleton className='h-2.5 w-16' />
				</div>
				<Skeleton className='h-3 w-64' />
				<Skeleton className='h-3 w-32' />
				<Skeleton className='h-5 w-20 rounded-full' />
			</div>
		))}
	</div>
);

/**
 * The empty state.
 *
 * An invitation: an account that has connected a channel and written rules will fill this page by
 * itself, and there is nothing for the reader to fix. The narrowed variant is a different message,
 * because "no rows in this window" is a fact about the filters, not about the agent.
 */
const ActionLogEmptyState = ({ isNarrowed }: { isNarrowed: boolean }) => (
	<div className='flex flex-col items-center gap-1.5 px-4 py-16 text-center'>
		<p className='text-sm text-foreground'>
			{isNarrowed ? 'No decisions in this view' : 'The log fills itself as comments arrive'}
		</p>
		<p className='max-w-md text-[13px] text-muted-foreground'>
			{isNarrowed
				? 'Widen the date range or clear a filter to see more of what the agent did.'
				: 'Every decision the agent makes on your connected accounts lands here, including the ones that took no action.'}
		</p>
	</div>
);

export default function ActionLog() {
	// Must stay in step with the route path: useUrlSearch rewrites the URL to this family.
	const { searchQuery, setSearchQuery, debouncedSearch } = useUrlSearch('q', '/agents/action-log');
	const [range, setRange] = useState<ActionLogDateRange>(EMPTY_ACTION_LOG_FILTERS.range);
	const [decisions, setDecisions] = useState<SocialActionDecision[]>(EMPTY_ACTION_LOG_FILTERS.decisions);
	const [actionTypes, setActionTypes] = useState<SocialActionType[]>(EMPTY_ACTION_LOG_FILTERS.actionTypes);
	const [channelIds, setChannelIds] = useState<string[]>(EMPTY_ACTION_LOG_FILTERS.channelIds);
	const [exportOpen, setExportOpen] = useState(false);
	/** The row whose panel is open. Null closes it — the list itself never changes. */
	const [selectedRow, setSelectedRow] = useState<SocialActionLogRow | null>(null);

	// Debounced, not raw: every keystroke would otherwise be an ILIKE scan of the window.
	const filters: ActionLogFilters = useMemo(
		() => ({ range, decisions, actionTypes, channelIds, search: debouncedSearch }),
		[range, decisions, actionTypes, channelIds, debouncedSearch]
	);

	const { rows, isLoading, isLoadingMore, error, hasMore, loadMore, retry, appliedWindow } = useActionLogs(filters);

	const { data: integrations } = useIntegrations();

	// Log rows carry a channel ID; the filter shows handles. The connected Meta accounts are the
	// only place that mapping exists, exactly as on the rules page.
	const metaChannels = useMemo(() => {
		const meta = (integrations ?? []).find((row) => row.name?.toLowerCase() === 'meta');
		return readMetaChannels(meta?.authMetaData);
	}, [integrations]);

	const channels: FilterableChannel[] = useMemo(
		() =>
			metaChannels.map((channel) => ({
				channelId: channel.channelId,
				handle: channel.handle,
				name: channel.name,
				platform: channel.platform
			})),
		[metaChannels]
	);

	const isNarrowed = hasNarrowedActionLog(filters);
	const showEmptyState = !isLoading && !error && rows.length === 0;

	return (
		/*
		 * Full width, like the rules page you arrive from. A 1200px cap left a band of dead screen
		 * on a large monitor while the Comment column truncated mid-sentence — the one column with
		 * no natural width, starved by the cap meant to protect it. The fixed columns stay grouped
		 * at the right whatever the width, so the extra space goes where it is wanted.
		 */
		<div className='flex h-full w-full flex-col'>
			{/*
			 * Header — Figma 4768:14489. The back control and the title on ONE centred line, 4px
			 * apart, and nothing under them.
			 *
			 * The subtitle that used to sit here is gone with the design's blessing: it restated the
			 * page's own name at a width that wrapped to three lines on a narrow window, and the
			 * empty state already says the same thing at the moment a reader actually needs it.
			 */}
			<div className='flex shrink-0 items-center justify-between gap-4 px-4 py-4'>
				<div className='flex items-center gap-1'>
					{/*
					 * A tertiary icon button BESIDE the title, not a labelled link stacked above it
					 * (design review, 20/08). This page is reached from a button on the rules page
					 * rather than from the sidebar, so the way back has to live here — but as a back
					 * control, not as a second heading competing with the title.
					 */}
					<Button asChild variant='ghost' size='icon-sm' className='shrink-0'>
						<Link to='/agents/social-media' aria-label='Back to the social media agent'>
							<ChevronLeft className='size-3.5' />
						</Link>
					</Button>
					<div className='flex min-w-0 flex-col'>
						{/* Plural, matching the "View action logs" button that leads here. The route
						    path stays singular — it is a URL people have bookmarked, not copy. */}
						<h1 className='text-base leading-6 font-semibold text-ink'>Action Logs</h1>
						{/* The trail the back arrow cannot show. The arrow says there IS a way back;
						    this says where back goes, which matters on a page reached from a button
						    rather than from the sidebar. */}
						<nav aria-label='Breadcrumb' className='text-xs leading-4 text-gray-800'>
							<Link to='/agents/social-media' className='hover:text-ink hover:underline'>
								Social Media Agent
							</Link>
							<span aria-hidden='true' className='px-1'>
								/
							</span>
							<span aria-current='page' className='text-ink'>
								Action Logs
							</span>
						</nav>
					</div>
				</div>

				{/* Search sits up here with the export, not down in the filter run — Figma 4768:14486
				    pairs them at 6px and keeps the filters a separate row of their own. */}
				<div className='flex shrink-0 items-center gap-1.5'>
					<SearchInput
						className='h-8 w-[280px]'
						placeholder='Search'
						maxLength={MAX_ACTION_LOG_SEARCH_LENGTH}
						value={searchQuery}
						onChange={(event) => setSearchQuery(event.target.value)}
					/>
					<Button
						variant='outline'
						className='h-8 shrink-0 rounded-full px-4 text-[13px] font-normal'
						onClick={() => setExportOpen(true)}
					>
						Export CSV
					</Button>
				</div>
			</div>

			<ActionLogFilterBar
				filters={filters}
				channels={channels}
				onRangeChange={setRange}
				onDecisionsChange={setDecisions}
				onActionTypesChange={setActionTypes}
				onChannelIdsChange={setChannelIds}
			/>

			<div className='flex-1 overflow-auto'>
				{isLoading && <ActionLogSkeleton />}

				{/*
				 * A failed first page is shown INSTEAD of the table. Rendering an empty table beside an
				 * error would say the agent did nothing, which is a different and much worse claim than
				 * "we could not read the log".
				 */}
				{!isLoading && error && rows.length === 0 && (
					<div className='flex flex-col items-center gap-3 px-4 py-16 text-center'>
						<p className='text-sm text-destructive'>{error}</p>
						<Button variant='outline' className={PILL_BUTTON} onClick={retry}>
							Try again
						</Button>
					</div>
				)}

				{showEmptyState && <ActionLogEmptyState isNarrowed={isNarrowed} />}

				{!isLoading && rows.length > 0 && (
					<>
						<ActionLogTable
							rows={rows}
							hasMore={hasMore}
							isLoadingMore={isLoadingMore}
							onLoadMore={loadMore}
							selectedId={selectedRow?.id ?? null}
							onSelect={setSelectedRow}
						/>
						{/* A failed "Load more" leaves the loaded rows alone and says so beneath them. */}
						{error && <p className='px-4 pb-4 text-[13px] text-destructive'>{error}</p>}
					</>
				)}
			</div>

			<ActionLogDetailPanel
				row={selectedRow}
				channels={metaChannels}
				onOpenChange={(open) => !open && setSelectedRow(null)}
			/>

			<ActionLogExportDialog
				open={exportOpen}
				onOpenChange={setExportOpen}
				filters={filters}
				appliedWindow={appliedWindow}
				loadedRowCount={rows.length}
				hasMore={hasMore}
			/>
		</div>
	);
}
